import { Link, Navigate } from "react-router-dom";
import { useEffect, useRef, useState } from "react";

import { GlofoxNav } from "@/components/portal/GlofoxNav";
import { env } from "@/config/env";
import { usePageTitle } from "@/hooks/usePageTitle";
import {
  createAccessRequest,
  listAccessRequests,
  type AccessRequest,
  type ApiPlatform,
} from "@/lib/api/access-requests";
import {
  canRequestAnotherAccess,
  EXPECTED_DAILY_VOLUME_OPTIONS,
  ACCESS_SCOPE_OPTIONS,
  formatAccessScopeLabel,
  getAccessRequestLoadErrorMessage,
  getAccessRequestSubmitErrorMessage,
  getAvailableIntegrations,
  getPlatformRequests,
  getRequestIntegrationsDisplay,
  INTEGRATION_OPTIONS,
  isAccessRequestFormValid,
  REQUESTER_TYPE_OPTIONS,
  toCreateAccessRequestPayload,
  validateScopeText,
  type AccessIntegration,
  type AccessRequestFormValues,
  type AccessScopeType,
} from "@/lib/api/access-request-form";
import { fetchPortalMe } from "@/lib/api/portal-me";
import {
  getAuthSession,
  updateAuthSessionUser,
} from "@/lib/auth/session";
import { useSandboxCredentialsEnabled } from "@/lib/feature-flags/useSandboxCredentialsEnabled";
import { GLOFOX_DOCS_HOME_ROUTE } from "@/lib/portal/docs-urls";
import {
  GLOFOX_REQUEST_ACCESS_DOCS_ROUTE,
  GLOFOX_REQUEST_ACCESS_HREF,
} from "@/lib/portal/glofox-routes";
import {
  PORTAL_HOME_ROUTE,
  PORTAL_LOGIN_LEGACY_ROUTE,
} from "@/lib/portal/portal-routes";

type PageState =
  | { kind: "loading" }
  | { kind: "ready" }
  | { kind: "load-error"; message: string };

const INITIAL_FORM: AccessRequestFormValues = {
  accessScopeType: "namespaces",
  intendedUseCase: "",
  locationIdsText: "",
  namespacesText: "",
  expectedDailyVolume: "",
  integration: "",
};

function createInitialForm(
  availableIntegrations: AccessIntegration[],
  includeRequesterType: boolean,
): AccessRequestFormValues {
  return {
    ...INITIAL_FORM,
    ...(includeRequesterType ? { requesterType: "customer" as const } : {}),
    integration: availableIntegrations[0] ?? "",
  };
}

function formatVolumeLabel(value: AccessRequest["expected_daily_volume"]): string {
  if (value == null) {
    return "Not provided";
  }
  return (
    EXPECTED_DAILY_VOLUME_OPTIONS.find((option) => option.value === value)
      ?.label ?? value
  );
}

function formatRequesterLabel(value: AccessRequest["requester_type"]): string {
  switch (value) {
    case "customer":
      return "Customer";
    case "partner":
      return "Partner";
    default:
      return value;
  }
}

function formatStatusLabel(status: AccessRequest["status"]): string {
  switch (status) {
    case "pending":
      return "Request submitted";
    case "approved":
      return "Approved";
    case "rejected":
      return "Denied";
    default:
      return status;
  }
}

const API_ACTIVATION_EMAIL = "apiactivation@abcfitness.com";
const ABC_FITNESS_PARTNER_PROGRAM_URL = "https://abcfitness.com/partners/";

function PartnerProgramCallout() {
  return (
    <div
      className="access-request-partner-callout"
      data-testid="gf-request-access-partner-callout"
      role="note"
    >
      <div className="access-request-partner-callout__header">
        <i className="ti ti-users" aria-hidden />
        <p className="access-request-partner-callout__title">Are you a partner?</p>
      </div>
      <p className="access-request-partner-callout__text">
        If you are not a partner yet, visit the ABC Fitness partner program.
      </p>
      <p className="access-request-partner-callout__note">
        *API access will only be granted if you are an approved partner.
      </p>
      <a
        className="access-request-partner-callout__btn"
        href={ABC_FITNESS_PARTNER_PROGRAM_URL}
        target="_blank"
        rel="noopener noreferrer"
        data-testid="gf-request-access-partner-program-link"
      >
        Visit partner program
      </a>
    </div>
  );
}

function formatScopeValues(values: string[]): string {
  return values.length > 0 ? values.join(", ") : "None";
}

function AccessRequestStatusCard({ request }: { request: AccessRequest }) {
  const statusClass =
    request.status === "approved"
      ? "access-status access-status--approved"
      : request.status === "rejected"
        ? "access-status access-status--rejected"
        : "access-status access-status--pending";

  const integrations = getRequestIntegrationsDisplay(request);

  return (
    <div className={statusClass} data-testid="gf-request-access-status">
      <div className="access-status__icon" aria-hidden>
        <i
          className={`ti ${
            request.status === "approved"
              ? "ti-circle-check"
              : request.status === "rejected"
                ? "ti-circle-x"
                : "ti-checks"
          }`}
        />
      </div>
      <h2>{formatStatusLabel(request.status)}</h2>
      <p>
        {request.status === "pending"
          ? "Your access request has been submitted. Our team will follow up by email — you don't need to keep this page open or wait for it to update."
          : request.status === "approved"
            ? "Your access request has been approved. You can start using the Glofox API with the credentials provided to you."
            : (
                <>
                  Your access request was not approved. Contact{" "}
                  <a href={`mailto:${API_ACTIVATION_EMAIL}`}>
                    {API_ACTIVATION_EMAIL}
                  </a>{" "}
                  if you have questions.
                </>
              )}
      </p>
      <dl className="access-status__details">
        <div>
          <dt>Requester type</dt>
          <dd>{formatRequesterLabel(request.requester_type)}</dd>
        </div>
        <div>
          <dt>Access scope</dt>
          <dd>{formatAccessScopeLabel(request.access_scope)}</dd>
        </div>
        {request.access_scope === "locations" ||
        (request.location_ids ?? []).length > 0 ? (
          <div className="access-status__full">
            <dt>Location IDs</dt>
            <dd>{formatScopeValues(request.location_ids ?? [])}</dd>
          </div>
        ) : null}
        {request.access_scope === "namespaces" ||
        (request.namespaces ?? []).length > 0 ? (
          <div className="access-status__full">
            <dt>Namespaces</dt>
            <dd>{formatScopeValues(request.namespaces ?? [])}</dd>
          </div>
        ) : null}
        <div>
          <dt>Expected daily volume</dt>
          <dd>{formatVolumeLabel(request.expected_daily_volume)}</dd>
        </div>
        <div className="access-status__full">
          <dt>Intended use case</dt>
          <dd>{request.intended_use_case}</dd>
        </div>
        <div>
          <dt>Integrations</dt>
          <dd>{integrations.join(", ")}</dd>
        </div>
        <div>
          <dt>Submitted</dt>
          <dd>{new Date(request.created_at).toLocaleString()}</dd>
        </div>
        {request.status === "rejected" && request.rejection_reason ? (
          <div
            className="access-status__full"
            data-testid="gf-request-access-rejection-reason"
          >
            <dt>Reason</dt>
            <dd>{request.rejection_reason}</dd>
          </div>
        ) : null}
      </dl>
    </div>
  );
}

function RequestAnotherAccessButton({ onClick }: { onClick: () => void }) {
  return (
    <div
      className="access-request-another"
      data-testid="gf-request-access-another"
    >
      <button
        type="button"
        className="submit-btn"
        data-testid="gf-request-access-another-submit"
        onClick={onClick}
      >
        <i className="ti ti-plus" /> Request another access
      </button>
    </div>
  );
}

function RequestAccessForm({
  apiPlatform,
  availableIntegrations,
  showLegacyRequesterType,
  onSubmitted,
  onCancel,
}: {
  apiPlatform: ApiPlatform;
  availableIntegrations: AccessIntegration[];
  showLegacyRequesterType: boolean;
  onSubmitted: (request: AccessRequest) => void;
  onCancel?: () => void;
}) {
  const [form, setForm] = useState<AccessRequestFormValues>(() =>
    createInitialForm(availableIntegrations, showLegacyRequesterType),
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const errorRef = useRef<HTMLDivElement>(null);

  const isValid = isAccessRequestFormValid(form, availableIntegrations);
  const scopeTextError = validateScopeText(
    form.accessScopeType === "locations"
      ? form.locationIdsText
      : form.namespacesText,
  );
  const showPartnerCallout =
    showLegacyRequesterType && form.requesterType === "partner";

  useEffect(() => {
    if (!error) {
      return;
    }
    errorRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }, [error]);

  function updateForm(patch: Partial<AccessRequestFormValues>) {
    setForm((current) => ({ ...current, ...patch }));
    setError(null);
  }

  async function handleSubmit(event?: { preventDefault(): void }) {
    event?.preventDefault();
    if (!isValid || loading) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await createAccessRequest(
        toCreateAccessRequestPayload(form, apiPlatform),
      );
      onSubmitted(response.request);
    } catch (err: unknown) {
      setError(getAccessRequestSubmitErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }

  return (
    <form
      className="access-request-form"
      data-testid="gf-request-access-form"
      noValidate
      onSubmit={(event) => {
        void handleSubmit(event);
      }}
    >
      {error ? (
        <div
          ref={errorRef}
          className="error-msg"
          data-testid="gf-request-access-error"
          role="alert"
        >
          <i className="ti ti-alert-circle" />
          <span>{error}</span>
        </div>
      ) : null}

      {showLegacyRequesterType ? (
        <>
          <fieldset className="field">
            <legend>
              Requester type <span className="req">*</span>
            </legend>
            <p className="field-hint">
              Tell us whether you are requesting access for your own business or as a
              partner building for customers.
            </p>
            <div className="vol-pills vol-pills--2">
              {REQUESTER_TYPE_OPTIONS.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  className={`vp${form.requesterType === option.value ? " on" : ""}`}
                  data-testid={`gf-request-access-requester-${option.value}`}
                  aria-pressed={form.requesterType === option.value}
                  onClick={() => updateForm({ requesterType: option.value })}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </fieldset>

          {showPartnerCallout ? <PartnerProgramCallout /> : null}
        </>
      ) : null}

      <div className="field">
        <label htmlFor="gf-request-access-scope">
          Access scope <span className="req">*</span>
        </label>
        <p className="field-hint">
          Choose whether you are requesting access by namespace or by location ID.
        </p>
        <select
          id="gf-request-access-scope"
          data-testid="gf-request-access-scope"
          value={form.accessScopeType}
          onChange={(event) =>
            updateForm({
              accessScopeType: event.target.value as AccessScopeType,
              locationIdsText: "",
              namespacesText: "",
            })
          }
        >
          {ACCESS_SCOPE_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>

      <div className={`field${scopeTextError ? " field--error" : ""}`}>
        <label htmlFor="gf-request-access-scope-values">
          {form.accessScopeType === "namespaces" ? "Namespaces" : "Location IDs"}{" "}
          <span className="req">*</span>
        </label>
        <p className="field-hint">
          Enter one or more values separated by commas.
        </p>
        {form.accessScopeType === "namespaces" ? (
          <textarea
            id="gf-request-access-scope-values"
            data-testid="gf-request-access-namespaces"
            placeholder="e.g. acme-fitness, beta-gym"
            value={form.namespacesText}
            onChange={(event) =>
              updateForm({ namespacesText: event.target.value })
            }
            rows={3}
            aria-invalid={scopeTextError ? true : undefined}
          />
        ) : (
          <textarea
            id="gf-request-access-scope-values"
            data-testid="gf-request-access-location-ids"
            placeholder="e.g. 5f8d0d55b54764421b7156c3"
            value={form.locationIdsText}
            onChange={(event) =>
              updateForm({ locationIdsText: event.target.value })
            }
            rows={3}
            aria-invalid={scopeTextError ? true : undefined}
          />
        )}
        {scopeTextError ? (
          <div className="field-error" data-testid="gf-request-access-scope-error" role="alert">
            <i className="ti ti-alert-circle" aria-hidden />
            <span>{scopeTextError}</span>
          </div>
        ) : null}
      </div>

      <div className="field">
        <label htmlFor="gf-request-access-use-case">
          Intended use case <span className="req">*</span>
        </label>
        <textarea
          id="gf-request-access-use-case"
          data-testid="gf-request-access-use-case"
          placeholder="Describe what you plan to build — e.g. syncing member data to a CRM, building a custom check-in kiosk, or automating membership renewals..."
          value={form.intendedUseCase}
          onChange={(event) => updateForm({ intendedUseCase: event.target.value })}
          rows={4}
        />
      </div>

      <fieldset className="field">
        <legend>
          Expected daily request volume <span className="req">*</span>
        </legend>
        <p className="field-hint">
          Select the range that best matches your anticipated usage.
        </p>
        <div className="vol-pills">
          {EXPECTED_DAILY_VOLUME_OPTIONS.map((option) => (
            <button
              key={option.value}
              type="button"
              className={`vp${
                form.expectedDailyVolume === option.value ? " on" : ""
              }`}
              data-testid={`gf-request-access-volume-${option.value}`}
              aria-pressed={form.expectedDailyVolume === option.value}
              onClick={() =>
                updateForm({ expectedDailyVolume: option.value })
              }
            >
              {option.label}
            </button>
          ))}
        </div>
      </fieldset>

      <div className="field">
        <label htmlFor="gf-request-access-integration">
          Integrations <span className="req">*</span>
        </label>
        <p className="field-hint">
          Select the access type you need. Options you have already requested are
          not shown.
        </p>
        <select
          id="gf-request-access-integration"
          data-testid="gf-request-access-integration"
          value={form.integration}
          onChange={(event) =>
            updateForm({
              integration: event.target.value as AccessIntegration | "",
            })
          }
        >
          {availableIntegrations.map((integration) => {
            const label =
              INTEGRATION_OPTIONS.find((option) => option.value === integration)
                ?.label ?? integration;

            return (
              <option key={integration} value={integration}>
                {label}
              </option>
            );
          })}
        </select>
      </div>

      <div className="submit-row">
        <p className="submit-note">
          Requests are reviewed manually. You will receive credentials once your
          request is approved.
        </p>
        {onCancel ? (
          <button
            type="button"
            className="access-request-link"
            data-testid="gf-request-access-cancel"
            onClick={onCancel}
          >
            Cancel
          </button>
        ) : null}
        <button
          type="submit"
          className="submit-btn"
          data-testid="gf-request-access-submit"
          disabled={!isValid || loading}
          style={{ opacity: isValid ? 1 : 0.5 }}
        >
          {loading ? (
            <>
              <i
                className="ti ti-loader-2"
                style={{ animation: "spin 1s linear infinite" }}
              />{" "}
              Submitting…
            </>
          ) : (
            <>
              <i className="ti ti-send" /> Submit request
            </>
          )}
        </button>
      </div>
    </form>
  );
}

export default function RequestAccessPage() {
  usePageTitle("Request access — Glofox");
  const sandboxCredentialsEnabled = useSandboxCredentialsEnabled();
  const showLegacyRequesterType = !sandboxCredentialsEnabled;
  const requestAccessRoute = sandboxCredentialsEnabled
    ? GLOFOX_REQUEST_ACCESS_HREF
    : GLOFOX_REQUEST_ACCESS_DOCS_ROUTE;
  const initialSession = getAuthSession();
  const sessionUserId = initialSession?.user.id;
  const apiPlatform = env.platform as ApiPlatform;

  const [pageState, setPageState] = useState<PageState>({ kind: "loading" });
  const [platformRequests, setPlatformRequests] = useState<AccessRequest[]>([]);
  const [showAnotherForm, setShowAnotherForm] = useState(false);

  useEffect(() => {
    if (!sessionUserId) {
      return;
    }

    let cancelled = false;

    async function loadPageData() {
      try {
        const [me, { requests }] = await Promise.all([
          fetchPortalMe(),
          listAccessRequests(),
        ]);
        if (cancelled) {
          return;
        }

        updateAuthSessionUser(me);
        setPlatformRequests(getPlatformRequests(requests, apiPlatform));
        setPageState({ kind: "ready" });
      } catch (err: unknown) {
        if (cancelled) {
          return;
        }
        setPageState({
          kind: "load-error",
          message: getAccessRequestLoadErrorMessage(err),
        });
      }
    }

    void loadPageData();

    return () => {
      cancelled = true;
    };
  }, [apiPlatform, sessionUserId]);

  if (!initialSession) {
    return (
      <Navigate
        to={
          sandboxCredentialsEnabled
            ? PORTAL_HOME_ROUTE
            : PORTAL_LOGIN_LEGACY_ROUTE
        }
        replace
        state={{ from: requestAccessRoute }}
      />
    );
  }

  const availableIntegrations = getAvailableIntegrations(
    platformRequests,
    apiPlatform,
  );
  const hasExistingRequests = platformRequests.length > 0;
  const canRequestMore = canRequestAnotherAccess(platformRequests, apiPlatform);
  const showForm =
    !hasExistingRequests || (showAnotherForm && availableIntegrations.length > 0);

  function handleRequestSubmitted(request: AccessRequest) {
    setPlatformRequests((current) => [...current, request]);
    setShowAnotherForm(false);
  }

  return (
    <>
      <GlofoxNav />
      <div className="portal-shell portal-shell--app">
        <main id="main-content" className="main-col">
          <div className="content doc-content">
            <div className="breadcrumb">
              <Link to={GLOFOX_DOCS_HOME_ROUTE} data-testid="gf-breadcrumb-home">
                Home
              </Link>
              <span className="sep">/</span>
              <Link to={GLOFOX_DOCS_HOME_ROUTE} data-testid="gf-breadcrumb-glofox">
                Glofox
              </Link>
              <span className="sep">/</span>
              <span data-testid="gf-breadcrumb-current">Request access</span>
            </div>

            <h1 data-testid="gf-request-access-title">Request access</h1>
            <p className="access-request-intro">
              Submit a request to access the Glofox API. Tell us about your
              integration plans and expected usage so we can provision the right
              credentials.
            </p>

            {pageState.kind === "loading" ? (
              <p
                className="access-request-loading"
                data-testid="gf-request-access-loading"
              >
                <i
                  className="ti ti-loader-2"
                  style={{ animation: "spin 1s linear infinite" }}
                />{" "}
                Loading your request…
              </p>
            ) : null}

            {pageState.kind === "load-error" ? (
              <div className="error-msg" data-testid="gf-request-access-load-error">
                <i className="ti ti-alert-circle" />
                <span>{pageState.message}</span>
              </div>
            ) : null}

            {pageState.kind === "ready" && hasExistingRequests && !showForm ? (
              <>
                <div
                  className="access-request-status-list"
                  data-testid="gf-request-access-status-list"
                >
                  {platformRequests.map((request) => (
                    <AccessRequestStatusCard key={request.id} request={request} />
                  ))}
                </div>
                {canRequestMore ? (
                  <RequestAnotherAccessButton
                    onClick={() => setShowAnotherForm(true)}
                  />
                ) : null}
              </>
            ) : null}

            {pageState.kind === "ready" && showForm ? (
              <RequestAccessForm
                key={availableIntegrations.join(",")}
                apiPlatform={apiPlatform}
                availableIntegrations={availableIntegrations}
                showLegacyRequesterType={showLegacyRequesterType}
                onSubmitted={handleRequestSubmitted}
                onCancel={
                  hasExistingRequests
                    ? () => setShowAnotherForm(false)
                    : undefined
                }
              />
            ) : null}

          </div>
        </main>
      </div>
    </>
  );
}
